import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Space } from 'antd';
import { LabeledDate } from 'client/ui/display/date/LabeledDate';
import { RequestApi } from 'client/components/schema/requests/RequestApi';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { LoadingPage } from 'client/ui/loader/LoadingPage';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { RequestLogic } from 'common/schema/RequestLogic';
import { PageSecondaryColumn } from 'client/ui/layout/page/PageSecondaryColumn';
import { ProcedureSummaryTitle } from 'client/routes/front/procedures/single/shared/ProcedureSummaryTitle';
import { PageFullRow } from 'client/ui/layout/page/PageFullRow';
import { ProcedureLinkText } from 'client/ui/naming/ProcedureLinkText';
import { ProcedureLogic } from 'common/schema/ProcedureLogic';
import { RequestDocumentMainColumn } from './RequestDocumentMainColumn';

interface Params {}

/**
 * Pagina per la visualizzazione del Singolo Documento Fascicolo
 * con i dettagli (standard) e gli allegati del documento
 * Visibile solo per gli utenti autenticati.
 *
 * TODO: Implementare permessi di visualizzazione
 * TODO: API per ottenere il fascicolo/documenti
 */
export interface RequestDocumentBasePageProps
  extends RouteComponentProps<Params> {
  id: number | null;
  token: string;
}

export function RequestDocumentBasePage(props: RequestDocumentBasePageProps) {
  const { id, token } = props;

  const { response, error, loading, refetch } = useApiQuery(RequestApi.find, {
    skip: id == null,
    data: {
      id: id!
    }
  });

  const request = response?.data;
  const isTypeEach = RequestLogic.isDossierTypeEach(request);

  useBreadcrumbItem({ path: '/richieste', title: 'Le mie Istanze' });
  useBreadcrumbItem({
    path: `/richieste/${id}/dettaglio`,
    title: 'Dettagli'
  });
  useBreadcrumbItem({
    path: `/richieste/${id}/dettaglio/fascicolo`,
    title: loading ? '...' : 'Fascicolo',
    disabled: !isTypeEach
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: 'Documento',
    loading: loading
  });

  if (error)
    return (
      <AlertPage
        showIcon
        type="error"
        message="Impossibile caricare il documento"
        description={getNetworkErrorMessage(
          error,
          'Si è verificato un errore. Riprovare.'
        )}
      />
    );

  if (!request || loading) return <LoadingPage />;

  return (
    <FrontLayout isUserPage>
      <PageFullRow>
        {/* Colonna Principale */}
        <RequestDocumentMainColumn request={request} token={token} />

        {/* Colonna Sommario / Azioni */}
        <PageSecondaryColumn orderMobile={3}>
          <Space direction="vertical" size={16}>
            <ProcedureSummaryTitle children={request.procedure.title} />
            <LabeledDate
              label="Scadenza"
              date={ProcedureLogic.getRenderableDateWith24Hour(
                request.procedure.expiresAt
              )}
            />
            <Link to={`/domande/${request.procedure.id}`}>
              {ProcedureLinkText}
            </Link>
            {/* <Link to={''}>Vai alle Faq</Link> */}
          </Space>
        </PageSecondaryColumn>
      </PageFullRow>
    </FrontLayout>
  );
}

// Per test e Design
// TODO: Estendere Tabella Output per inserire dati di visualizzazione
// che saranno impostati in un Tab dedicato lato Admin (Fascicolo??) :
// visible: boolean (default false) = Indica se il campo sarà mostrato nel dettaglio (solo se valorizzato)
// Andrà configurato anche la scelta del campo del documentale (output) che andrà visualizzato in evidenza nella pagina (come status)
const documentDetail = [
  {
    id: 180,
    procedure_id: 13,
    key: 'std_titolo',
    datatype: 'Text',
    target: 'Document',
    label: 'Oggetto del Documento',
    visible: true,
    text: 'Testo Esempio Usu ne denique albucius gloriatur.'
  },
  {
    id: 181,
    procedure_id: 13,
    key: 'std_descrizione',
    datatype: 'Text',
    target: 'Document',
    label: 'Descrizione',
    visible: true,
    text:
      'Ipsum tibique deseruisse vel ut. Ad laudem iracundia eam. Eum id legere scripta nominavi, vim melius ceteros et, ea tale enim nec.'
  },
  {
    id: 181,
    procedure_id: 13,
    key: 'std_data',
    datatype: 'Date',
    target: 'Document',
    label: 'Data',
    visible: true,
    text: new Date()
  },
  {
    id: 181,
    procedure_id: 13,
    key: 'std_mittente_nome',
    datatype: 'Text',
    target: 'Document',
    label: 'Nome Autore',
    visible: false,
    text: 'Testo Esempio'
  },
  {
    id: 182,
    procedure_id: 13,
    key: 'std_mittente_cognome',
    datatype: 'Text',
    target: 'Document',
    label: 'Cognome Autore',
    visible: false,
    text: 'Testo Esempio'
  },
  {
    id: 183,
    procedure_id: 13,
    key: 'std_mittente_email',
    datatype: 'Text',
    target: 'Document',
    label: 'E-Mail Autore',
    visible: false,
    text: 'Testo Esempio'
  }
];
